import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, viewChildren } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CardMode } from '@components/card/card.component';
import { MdsExpansionPanelComponent, MdsTagComponent } from '@medpacesoftwaredevelopment/designsystem';
import { Site } from '@models/site';
import { VisitName } from '@models/study';
import { AdminStudyServices } from '@services/admin/admin-study.sevice';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { CountryStateService } from '@services/state-management/country-state.service';
import { CurrencyStateService } from '@services/state-management/currency-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { rxjsComputed } from '@utility/utility';
import { combineLatest, EMPTY, filter, map, switchMap } from 'rxjs';
import { SingleStipendVisitComponent } from './single-stipend-visit/single-stipend-visit.component';
import { SingleStipendVisitFormGroup } from './single-stipend-visit/single-stipend-visit.form';
import { StipendVisitsFormGroup } from './stipend-visits.form';

@Component({
    selector: 'medpace-stipend-visits',
    imports: [AsyncPipe, SingleStipendVisitComponent, MdsTagComponent, MdsExpansionPanelComponent],
    templateUrl: './stipend-visits.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StipendVisitsComponent {
    studyService = inject(AdminStudyServices);
    siteStateService = inject(SiteStateService);
    currencyService = inject(CurrencyStateService);
    countryService = inject(CountryStateService);
    snackbarService = inject(SnackbarService);

    formGrp = input.required<StipendVisitsFormGroup>();
    mode = input.required<CardMode>();
    site = input<Site>();
    site$ = toObservable(this.site).pipe(filter(Boolean));
    studyVisits$ = this.site$.pipe(switchMap((site) => this.studyService.getStudyVisitTypes(site.studyId)));

    currencyCode$ = rxjsComputed(() =>
        this.siteStateService.getSiteCountry().pipe(
            switchMap((country) => {
                if (!!country)
                    return this.countryService.getCountryByCodeOrName(country).pipe(
                        map((country) => {
                            if (!country?.currencyIsoCode) {
                                this.snackbarService.openErrorSnackbar('Missing currency! Stipend cannot be created');
                                return '';
                            }

                            return country.currencyIsoCode;
                        })
                    );
                return EMPTY;
            })
        )
    );

    studyVisitsGroupedByVisitGroupId$ = rxjsComputed(() =>
        this.studyVisits$.pipe(
            map((studyVisits) => {
                // groupBy , will return object like { 1: [VisitName1, VisitName2,...], 10001: [...] }
                return studyVisits.reduce<Record<number, VisitName[]>>((acc, curr) => {
                    // group by visitGroupId
                    const key = curr.visitGroupId;
                    // create the group if non-existent
                    if (!acc[key]) acc[key] = [];
                    //add to the group
                    acc[key].push(curr);

                    return acc;
                }, {});
            })
        )
    );

    // VMs to be rendered in html
    studyVisitGroupsVMs$ = rxjsComputed(() =>
        combineLatest({
            studyVisitsGroupedByVisitGroupId: this.studyVisitsGroupedByVisitGroupId$.source$,
            site: this.site$,
        }).pipe(
            map(({ site, studyVisitsGroupedByVisitGroupId }) => {
                this.formGrp().controls.stipendVisits.clear();

                return Object.values(studyVisitsGroupedByVisitGroupId).map((studyVisits) => {
                    const title = studyVisits.at(0)?.visitGroup.name;
                    if (!title) throw new Error('Visit group missing');
                    const studyVisitVMs = studyVisits.map((studyVisit) => {
                        const existingStipend = site.siteVisitStipends.find((x) => x.visitId === studyVisit.id);
                        const formGroup = new SingleStipendVisitFormGroup();
                        this.formGrp().controls.stipendVisits.push(formGroup);
                        const studyVisitVM = {
                            existingStipend,
                            formGroup,
                            studyVisit,
                        };
                        return studyVisitVM;
                    });
                    const groupVM = {
                        studyVisitVMs,
                        title,
                    };
                    return groupVM;
                });
            })
        )
    );
    singleStipendVisitComponents = viewChildren(SingleStipendVisitComponent);

    push(site: Site) {
        this.singleStipendVisitComponents().forEach((stipendVisit) => stipendVisit.push(site));

        return site;
    }
}
